import { OperationSupervision } from 'models/OperationRequest';
import { Store } from 'models/Store';

export type SupervisionMap = Record<string, string>;

export const storeKey = (store: Pick<Store, 'id'>) => String(store.id);

export const storeLocation = (store: Store) =>
  store.regionKind === 'CAPITAL'
    ? store.commune || store.regionLabel
    : store.regionLabel;

/** Con un solo supervisor todas las salas quedan con él. */
export const effectiveMap = (
  stores: Pick<Store, 'id'>[],
  sups: string[],
  map: SupervisionMap
): SupervisionMap => {
  if (sups.length !== 1) return map;
  return Object.fromEntries(stores.map((s) => [storeKey(s), sups[0]]));
};

export const hasSupervisor = (
  store: Pick<Store, 'id'>,
  sups: string[],
  eff: SupervisionMap
) => {
  const assigned = eff[storeKey(store)];
  return !!assigned && sups.includes(assigned);
};

export const missingStores = <T extends Pick<Store, 'id'>>(
  stores: T[],
  sups: string[],
  eff: SupervisionMap
) => stores.filter((s) => !hasSupervisor(s, sups, eff));

/** Reparto round-robin en el orden recibido. */
export const autoSplit = (
  stores: Pick<Store, 'id'>[],
  sups: string[]
): SupervisionMap =>
  sups.length === 0
    ? {}
    : Object.fromEntries(
        stores.map((s, i) => [storeKey(s), sups[i % sups.length]])
      );

/** Supervisor compartido por todas las salas, o null si difieren. */
export const commonSupervisor = (
  stores: Pick<Store, 'id'>[],
  eff: SupervisionMap
): string | null => {
  if (stores.length === 0) return null;
  const first = eff[storeKey(stores[0])];
  if (!first) return null;
  return stores.every((s) => eff[storeKey(s)] === first) ? first : null;
};

export const countFor = (
  stores: Pick<Store, 'id'>[],
  eff: SupervisionMap,
  sup: string
) => stores.filter((s) => eff[storeKey(s)] === sup).length;

export const buildSupervisionPayload = (
  stores: Pick<Store, 'id'>[],
  sups: string[],
  map: SupervisionMap
): OperationSupervision => {
  const eff = effectiveMap(stores, sups, map);
  const clean: SupervisionMap = {};
  stores.forEach((s) => {
    const key = storeKey(s);
    if (eff[key] && sups.includes(eff[key])) clean[key] = eff[key];
  });
  return { sups, map: clean };
};

export const firstName = (fullName: string) => fullName.split(' ')[0];
